<script lang="ts">
import type { ClassValue, DataGridColumn } from './VDataGridColumnV2.vue';
import VIcon                   from "@/lib/components/VIcon/VIcon.vue";
import VButton                 from "@/lib/components/VButton/VButton.vue";

/**
 * VDataGridRowV2 — рендерит одну строку таблицы.
 *
 * Контрол выбора (чекбокс или action-кнопка) позиционируется автоматически:
 * - checkbox → слева (первая ячейка)
 * - action   → справа (последняя ячейка)
 * - none     → без контрола, выделение только кликом по строке
 *
 * Режим draggable включает HTML5 drag-n-drop и эмитит события
 * drag:start / drop с полезной нагрузкой для внешнего обработчика.
 */
export default defineComponent({
  name: 'VDataGridRowV2',

  props: {
    row: {
      type: Object as PropType<Record<string, unknown>>,
      required: true,
    },
    index: {
      type: Number,
      default: null
    },
    columns: {
      type: Array as PropType<DataGridColumn[]>,
      required: true,
    },
    selectable: {
      type: Boolean,
      default: false,
    },
    /**
     * Режим контрола выбора строк:
     * - 'checkbox' — стандартный чекбокс слева
     * - 'action'   — кнопка add/delete справа
     * - 'none'     — без визуального контрола, выделение только кликом по строке
     */
    selectionControl: {
      type: String as PropType<'checkbox' | 'action' | 'none'>,
      default: 'checkbox',
    },
    isSelected: {
      type: Boolean,
      default: false,
    },
    isDisabled: {
      type: Boolean,
      default: false,
    },
		/** Причина disabled строки — текст тултипа на mouseenter */
		disabledReason: {
			type: String,
			default: '',
		},
    isClickable: {
      type: Boolean,
      default: false,
    },
    /** Включить HTML5 drag-n-drop для сортировки строк */
    draggable: {
      type: Boolean,
      default: false,
    },

  },

  emits: [
    'click',
    'dblclick',
    'contextmenu',
    'select',
    'drag:start',
    'drop',
  ],

  data() {
    return {
      // Класс наводки при drag-over: 'before' | 'after' | null
      dragHoverPosition: null as 'before' | 'after' | null,
    };
  },

  methods: {
    hasClassName(value: ClassValue | null, name: string): boolean {
      if (!value) return false;
      if (typeof value === 'string') return value.split(/\s+/).includes(name);
      if (Array.isArray(value)) return value.some(v => this.hasClassName(v as ClassValue, name));
      return value[name] === true;
    },

		renderCell(col: DataGridColumn): VNode {
			const value = this.row[col.id];

			const cellClass = typeof col.contentClass === 'function'
				? col.contentClass(this.row)
				: col.contentClass;

			const hasEllipsis = this.hasClassName(cellClass, 'ellipsis');

			const tdOptions: Record<string, unknown> = {
				key:              col.id,
				class:            [cellClass, { editable: col.onEdit !== null }],
				style:            {
					textAlign: col.contentAlign,
					maxWidth:  hasEllipsis ? col.width : null,
				},
				'data-column-id': col.id,
			};

			if (col.onEdit) {
				tdOptions.onClick = (event: MouseEvent) => {
					event.stopPropagation();
					col.onEdit(this.row, event.currentTarget as HTMLElement);
				};
			}

			const cellContent: VNode[] = [];

			if (col.cellSlot) {
				cellContent.push(
					...col.cellSlot({ row: this.row, index: this.index, value, isSelected: this.isSelected, isDisabled: this.isDisabled }),
				);
			}
			else {
				cellContent.push(String(value ?? ''));
			}

			if (col.onEdit) {
				cellContent.push(
					h(VIcon, { material: col.editMaterial, class: 'edit-pencil' }),
				);
			}

			return h('td', tdOptions, cellContent);
		},

    /** Рендерит чекбокс выбора (для selectionControl = 'checkbox') */
    renderCheckboxCell(): VNode {
      return h('td', { key: '__checkbox__', class: 'chb' }, [
        h('input', {
          type: 'checkbox',
          checked: this.isSelected,
        }),
        h('button', {
          class: 'chb-btn',
          onClick: (e: MouseEvent) => {
            e.stopPropagation();
            this.$emit('select');
          },
        }, [
          h('span', { class: 'material-icons mds-icon' }, 'check'),
        ]),
      ]);
    },

    /** Рендерит кнопку add/delete (для selectionControl = 'action') */
    renderActionCell(): VNode {
      const icon = this.isSelected ? 'delete' : 'add';

      return h('td', { key: '__action__', class: 'chb' }, [
        h(VButton, {
          disabled: !this.isSelected && this.isDisabled,
          onClick: (e: MouseEvent) => {
            e.stopPropagation();
            this.$emit('select');
          },
        }, {
          icon: () => h(VIcon, { material: icon }),
        }),
      ]);
    },

    // ==================== Drag and drop ====================

    onDragStart(event: DragEvent) {
      if (!event.dataTransfer) return;

      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('text/plain', String(this.row.id ?? ''));

      this.$emit('drag:start', { row: this.row, event });
    },

    onDragOver(event: DragEvent) {
      event.preventDefault();
      if (event.dataTransfer) {
        event.dataTransfer.dropEffect = 'move';
      }

      const target = event.currentTarget as HTMLElement;
      const rect = target.getBoundingClientRect();
      const midpoint = rect.y + rect.height * 0.5;

      this.dragHoverPosition = event.clientY > midpoint ? 'after' : 'before';
    },

    onDragLeave() {
      this.dragHoverPosition = null;
    },

    onDrop(event: DragEvent) {
      event.preventDefault();
      event.stopPropagation();

      const pasteMode = this.dragHoverPosition ?? 'before';
      this.dragHoverPosition = null;

      this.$emit('drop', { row: this.row, pasteMode, event });
    },
  },

  render(): VNode {
    const cells: VNode[] = [];

    // Чекбокс — всегда слева
    if (this.selectable && this.selectionControl === 'checkbox') {
      cells.push(this.renderCheckboxCell());
    }

    // Все колонки данных
    for (const col of this.columns) {
      cells.push(this.renderCell(col));
    }

    // Action-кнопка — всегда справа
    if (this.selectable && this.selectionControl === 'action') {
      cells.push(this.renderActionCell());
    }

    const handlers: Record<string, unknown> = {
      onClick: (e: MouseEvent) => {
        this.$emit('click', e);

        // В режиме выбора вся строка кликабельна, не только контрол
        if (this.selectable) {
          e.stopPropagation();
          this.$emit('select');
        }
      },
      onDblclick:    (e: MouseEvent) => this.$emit('dblclick', e),
      onContextmenu: (e: MouseEvent) => this.$emit('contextmenu', e),
			onMouseenter: () => {
				if (this.isDisabled && this.disabledReason) {
					this.$core.tooltip.show(this.$el, this.disabledReason);
				} else {
					// строка не disabled или причины нет — тултип не нужен
				}
			},
			onMouseleave: () => { this.$core.tooltip.hide(); }
    };

    if (this.draggable) {
      handlers.onDragstart = this.onDragStart;
      handlers.onDragover  = this.onDragOver;
      handlers.onDragleave = this.onDragLeave;
      handlers.onDrop      = this.onDrop;
    }

    return h('tr', {
      class: {
        clickable: !this.isDisabled && this.isClickable,
        selected:  this.isSelected,
        disabled:  this.isDisabled,
        'drag-hovered': this.dragHoverPosition !== null,
        'paste-before': this.dragHoverPosition === 'before',
        'paste-after':  this.dragHoverPosition === 'after',
      },
      draggable: this.draggable,
      ...handlers,
    }, cells);
  },
});
</script>
